import Alpine from "alpinejs";
import axios from "axios";

document.addEventListener("alpine:init", () => {
  Alpine.store("globalSearch", {
    // Core properties
    id: null,
    culture: null,
    carlistUrl: null,
    isLoading: false,
    resultsChanging: false,
    displayCount: 0,

    // Sort configuration
    sort: false,
    sortItems: {
     // Date: "Nyeste biler",
      Model: "Model",
      Brand: "Mærke",
      PriceLow: "Laveste pris",
      PriceHigh: "Højeste pris",
      Year: "Modelår",
      Mileage: "Kilometer",
    },

    // Default search state - single source of truth
    defaultSearch: {
      search: "",
      makes: [],
      carModels: [],
      equipment: [],
      propellants: [],
      leasingType: "",
      gear: "",
      types: [],
      isLeasing: true,
      isEngros: true,
      isNoTax: true,
      yellowPlates: null,
      leasingOnly: false,
      maxLeasingPrice: 0,
      maxTaxationBasis: 0,
      sort: "",
      desc: false
    },

    // Current search state - initialized as a copy of defaultSearch
    search: null,

    // Filter state
    filter: {
      types: [],
      makes: [],
      makesGrouped: {},
      models: [],
      propellants: [],
      equipment: [],
      maxPrice: 0,
      minPrice: 0,
      leasingMaxPrice: 0,
      leasingMinPrice: 0,
      maxTaxationBasis: 0,
      minTaxationBasis: 0,
      years: [],
      maxMilage: 0,
      minMilage: 0,
      maxHK: 0,
      minHK: 0,
      doors: [],
      colors: [],
      gearTypes: [],
      leasingCount: 0,
      engrosCount: 0,
      noTaxCount: 0,
      yellowPlateCount: 0,
      totalCount: 0,
      allCount: 0
    },

    // Initialize the store
    async initialize(id, culture, carlistUrl = "/biler/") {
      this.id = id;
      this.culture = culture;
      this.carlistUrl = carlistUrl;
      // Initialize search as a deep copy of defaultSearch
      this.search = JSON.parse(JSON.stringify(this.defaultSearch));
      await this.loadFiltersFromUrl();
      await this.getFilters(id, culture);
      this.setupEventListeners();
    },

    // Set up event listeners
    setupEventListeners() {
      window.addEventListener("popstate", async () => {
        await this.loadFiltersFromUrl();
        await this.fetchAndUpdateContent(window.location.href);
      });
    },

    // Load filters from URL parameters
    async loadFiltersFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAnyParams = Array.from(urlParams.keys()).length > 0;
      if (!hasAnyParams) {
        // No filters in URL, reset to default
        this.search = JSON.parse(JSON.stringify(this.defaultSearch));
        return;
      }
      // Helper function to safely get array values
      const getArrayValue = (param) => {
        if (urlParams.has(param)) {
          const value = urlParams.get(param);
          return value ? value.split(",") : [];
        }
        return this.search[param] || [];
      };
      // Helper function to safely get boolean values
      const getBooleanValue = (param) => urlParams.has(param) ? urlParams.get(param) !== "false" : this.search[param];
      // Helper function to safely get numeric values
      const getNumericValue = (param) => urlParams.has(param) ? parseInt(urlParams.get(param), 10) : this.search[param];
      // Update search parameters
      this.search = {
        ...this.search,
        search: urlParams.get("q") || this.search.search,
        makes: getArrayValue("m"),
        carModels: getArrayValue("c"),
        equipment: getArrayValue("eq"),
        propellants: getArrayValue("p"),
        leasingType: urlParams.get("lt") || this.search.leasingType,
        gear: urlParams.get("g") || this.search.gear,
        types: getArrayValue("t"),
        isLeasing: getBooleanValue("l"),
        isEngros: getBooleanValue("e"),
        isNoTax: getBooleanValue("n"),
        yellowPlates: this.parseYellowPlates(urlParams.get("y")),
        sort: urlParams.get("s") || this.search.sort,
        leasingOnly: urlParams.get("lo") === "true",
        maxLeasingPrice: getNumericValue("lp"),
        maxTaxationBasis: getNumericValue("tb")
      };
      // Safeguard: always include these fields as true if undefined or null
      this.search.isLeasing = this.search.isLeasing ?? true;
      this.search.isEngros = this.search.isEngros ?? true;
      this.search.isNoTax = this.search.isNoTax ?? true;
    },

    // Parse yellow plates value
    parseYellowPlates(value) {
      if (value === null) return null;
      return value === "true";
    },

    // Fetch filters from API
    async getFilters(id = this.id, culture = this.culture) {
      if (!id || !culture) {
        throw new Error("ID or culture is not set");
      }

      this.isLoading = true;
      try {
        const response = await axios.post("/api/search/filters", this.search, {
          params: { id, culture },
        });

        //console.log(response.data);

        if (response.data?.filters) {
          this.filter = { ...response.data.filters };
          this.displayCount = this.filter.totalCount;
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        this.handleApiError(error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Handle API errors
    handleApiError(error) {
      console.error("Error fetching filters:", error);
      if (error.response) {
        console.error("Server responded with error:", error.response.status);
        console.error("Error data:", error.response.data);
      } else if (error.request) {
        console.error("No response received from server");
      } else {
        console.error("Error setting up the request:", error.message);
      }
    },

    // Get joined string of selected items
    getSelectedItemsJoined(arrayName, defaultText = "Select items") {
      const array = this.search[arrayName] || [];
      return array.length > 0 ? array.join(", ") : defaultText;
    },

    // Check if any filters are active
    hasActiveFilters() {
      return Object.entries(this.search).some(([key, value]) => {
        const defaultValue = this.defaultSearch[key];
        // Treat null and undefined as equal
        if (value == null && defaultValue == null) return false;
        if (Array.isArray(defaultValue)) {
          return JSON.stringify(value) !== JSON.stringify(defaultValue);
        }
        return value !== defaultValue;
      });
    },

    // Update search parameters
    setSearch(newSearch) {
      // Handle array updates properly
      Object.entries(newSearch).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Ensure we're working with a copy of the array
          this.search[key] = [...value];
        } else {
          this.search[key] = value;
        }
      });
    },

    // Toggle array value (helper method for checkboxes)
    async toggleArrayValue(key, value) {
      const currentArray = this.search[key] || [];
      const index = currentArray.indexOf(value);
      
      if (index === -1) {
        // Add value if not present
        this.search[key] = [...currentArray, value];
      } else {
        // Remove value if present
        this.search[key] = currentArray.filter(item => item !== value);
      }
      
      await this.getFilters();
      return this.search[key];
    },

    // Generate search query string
    searchQuery() {
      const queryObject = {
        q: this.search.search,
        m: this.search.makes,
        c: this.search.carModels,
        p: this.search.propellants,
        eq: this.search.equipment,
        g: this.search.gear,
        t: this.search.types,
        lt: this.search.leasingType,
        lp: this.search.maxLeasingPrice,
        tb: this.search.maxTaxationBasis,
        l: this.search.isLeasing,
        e: this.search.isEngros,
        n: this.search.isNoTax,
        y: this.search.yellowPlates,
        s: this.search.sort,
        lo: this.search.leasingOnly,
      };

      return "?" + this.objectToQueryString(queryObject);
    },

    // Convert object to query string
    objectToQueryString(obj) {
      return Object.entries(obj)
        .filter(([_, value]) => {
          // Filter out null, undefined, empty strings, false, empty arrays, and 0
          if (value == null || value === "" || value === false) return false;
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === "number") return value !== 0;
          return true;
        })
        .map(([key, value]) => {
          const encodedValue = Array.isArray(value)
            ? value.map(x => encodeURIComponent(x)).join(",")
            : encodeURIComponent(value);
          return `${encodeURIComponent(key)}=${encodedValue}`;
        })
        .join("&");
    },

    // Clear all search parameters
    async clearSearch() {
      // Create a new copy of defaultSearch
      this.search = JSON.parse(JSON.stringify(this.defaultSearch));
      await this.getFilters(this.id, this.culture);
      this.displayCount = this.filter.totalCount;
    },

    // Submit search with page reload
    submitSearch(url = this.carlistUrl, includeQuery = true) {
      const fullUrl = includeQuery ? `${url}${this.searchQuery()}` : url;
      window.location.href = fullUrl;
    },

    // Submit search without page reload
    async submitSearchWithPushState(url = this.carlistUrl, includeQuery = true) {
      const fullUrl = includeQuery ? `${url}${this.searchQuery()}` : url;
      history.pushState({}, "", fullUrl);
      await this.fetchAndUpdateContent(fullUrl);
    },

    // Fetch and update content without page reload
    async fetchAndUpdateContent(url) {
      this.resultsChanging = true;
      try {
        const response = await axios.get(url);
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, "text/html");
        const newContent = doc.getElementById("search-result");
        
        if (newContent) {
          document.getElementById("search-result").innerHTML = newContent.innerHTML;
          window.dispatchEvent(new Event("searchResultUpdated"));
        }
      } catch (error) {
        console.error("Error fetching updated content:", error);
      } finally {
        this.resultsChanging = false;
      }
    },

    // Add debounced getFilters method
    debouncedGetFilters: Alpine.debounce(async function() {
      await this.getFilters();
    }, 300)
  });
});
